import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

/* Native Android alarms for Aura Codex pillars — scheduled through the OS
   AlarmManager so they ring with sound + vibration even when Bliss is
   closed. Re-planned (next DAYS days) whenever the Codex state changes. */

const CHANNEL = 'bliss-alarms-v2';   // channel sound is fixed at creation → new id
const BASE_ID = 700000;
const DAYS = 4;
const EXACT_ASKED_KEY = 'bliss_exact_alarm_asked_v1';
const PILLAR_ORDER = ['power', 'fuel', 'force', 'energy', 'destiny', 'aegis'];
const LABELS = { power: 'Power', fuel: 'Fuel', force: 'Force', energy: 'Energy', destiny: 'Destiny', aegis: 'Aegis' };

export const nativeAlarmsAvailable = (() => {
  try { return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('LocalNotifications'); }
  catch (_) { return false; }
})();

let ready = null;
const ensureReady = () => {
  if (ready) return ready;
  ready = (async () => {
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') perm = await LocalNotifications.requestPermissions();
    if (perm.display !== 'granted') { ready = null; return false; }
    try {
      await LocalNotifications.createChannel({
        id: CHANNEL, name: 'Bliss Alarms', description: 'Aura Codex pillar alarms',
        importance: 5, visibility: 1, vibration: true, lights: true, lightColor: '#D4AF37',
        sound: 'bliss_alarm.wav',   // res/raw/bliss_alarm.wav — 30 s Bliss anthem
      });
    } catch (_) { /* channel exists */ }
    return true;
  })().catch(() => { ready = null; return false; });
  return ready;
};

/* Exact timing needs "Alarms & reminders" (Android 12+). The plugin opens
   that setting itself when an exact alarm is scheduled — allow it ONCE; if
   the user declines, fall back to inexact so we never nag. */
const wantExact = async () => {
  try {
    const s = await LocalNotifications.checkExactNotificationSetting();
    if (s?.exact_alarm === 'granted') return true;
    if (localStorage.getItem(EXACT_ASKED_KEY)) return false;
    localStorage.setItem(EXACT_ASKED_KEY, '1');
    return true;
  } catch (_) { return true; }
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const buildPlan = (state) => {
  const out = [];
  const now = Date.now();
  const pillars = state?.pillars || {};
  const fusedToday = state?.date === todayKey();
  PILLAR_ORDER.forEach((pid, pIdx) => {
    const ps = pillars[pid];
    if (!ps) return;
    const alarms = Array.isArray(ps.alarms) ? ps.alarms : (ps.window?.start ? [ps.window.start] : []);
    alarms.slice(0, 5).forEach((hhmm, aIdx) => {
      const [h, m] = String(hhmm || '').split(':').map((n) => parseInt(n, 10));
      if (Number.isNaN(h) || Number.isNaN(m)) return;
      for (let day = 0; day < DAYS; day += 1) {
        if (day === 0 && fusedToday && ps.fused) continue;
        const at = new Date();
        at.setDate(at.getDate() + day);
        at.setHours(h, m, 0, 0);
        if (at.getTime() <= now + 1000) continue;
        const label = LABELS[pid] || 'Codex';
        out.push({
          id: BASE_ID + pIdx * 100 + aIdx * 10 + day,
          title: `Bliss AuraCodex · ${label} Time`,
          body: `Time for ${label} — Fuse it in the ${label} tile.`,
          channelId: CHANNEL,
          sound: 'bliss_alarm.wav',
          schedule: { at, allowWhileIdle: true },
          extra: { pillarId: pid },
        });
      }
    });
  });
  return out;
};

export const syncNativeAlarms = async (state) => {
  if (!nativeAlarmsAvailable) return;
  try {
    const plan = buildPlan(state);
    const { notifications = [] } = await LocalNotifications.getPending();
    const ours = notifications.filter((n) => n.id >= BASE_ID && n.id < BASE_ID + 1000);
    if (ours.length) await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
    if (!plan.length) return;
    if (!(await ensureReady())) return;
    const exact = await wantExact();
    await LocalNotifications.schedule({
      notifications: plan.map((n) => ({ ...n, isExactNotification: exact })),
    });
  } catch (_) { /* never block the UI on native failures */ }
};

/* App in foreground: the in-app anthem is already playing, so clear the
   OS copy to avoid two sounds at once. */
export const dismissDeliveredAlarms = async () => {
  if (!nativeAlarmsAvailable) return;
  try {
    const { notifications = [] } = await LocalNotifications.getDeliveredNotifications();
    const ours = notifications.filter((n) => n.id >= BASE_ID && n.id < BASE_ID + 1000);
    if (ours.length) await LocalNotifications.removeDeliveredNotifications({ notifications: ours });
  } catch (_) { /* noop */ }
};

/* User tapped an alarm notification → app opens; report which pillar. */
export const onAlarmNotificationTap = (cb) => {
  if (!nativeAlarmsAvailable) return () => {};
  let handle = null;
  LocalNotifications.addListener('localNotificationActionPerformed', (ev) => {
    const pid = ev?.notification?.extra?.pillarId;
    if (pid) cb(pid);
  }).then((h) => { handle = h; }).catch(() => {});
  return () => { try { handle?.remove(); } catch (_) { /* noop */ } };
};
